import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft, Calculator, Home, DollarSign, MapPin,
  Bed, Bath, Square, Calendar, Sparkles
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import { isFreeTier } from "@/utils/planMessaging";

const RentEstimator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const isFreeInvestor = isFreeTier(user?.tier);
  const propertyTypeOptions = [
    { value: "single_family", label: "Single Family" },
    { value: "duplex", label: "Duplex" },
    { value: "triplex", label: "Triplex" },
    { value: "fourplex", label: "Fourplex" },
    { value: "condo", label: "Condo" },
    { value: "townhouse", label: "Townhouse" },
  ];
  
  const [formData, setFormData] = useState({
    property_type: "single_family",
    bedrooms: 3,
    bathrooms: 2,
    square_footage: 1500,
    year_built: 2000,
    condition: "average",
    amenities: [],
    location_quality: "average"
  });

  useEffect(() => {
    if (isFreeInvestor && formData.property_type !== "single_family") {
      setFormData(prev => ({ ...prev, property_type: "single_family" }));
      setResult(null);
    }
  }, [isFreeInvestor, formData.property_type]);

  const amenityOptions = [
    { value: "garage", label: "Garage" },
    { value: "pool", label: "Pool" },
    { value: "updated_kitchen", label: "Updated Kitchen" },
    { value: "updated_bathrooms", label: "Updated Bathrooms" },
    { value: "hardwood_floors", label: "Hardwood Floors" },
    { value: "central_ac", label: "Central A/C" },
    { value: "washer_dryer", label: "Washer/Dryer" },
    { value: "fireplace", label: "Fireplace" },
    { value: "fenced_yard", label: "Fenced Yard" },
    { value: "pet_friendly", label: "Pet Friendly" },
    { value: "smart_home", label: "Smart Home Features" },
    { value: "ev_charging", label: "EV Charging" }
  ];

  const handleInputChange = (field, value) => {
    if (field === "property_type" && isFreeInvestor && value !== "single_family") {
      toast.error("Free Investor plan only supports single-family rent estimates. Upgrade to Starter or Pro for other property types.");
      value = "single_family";
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    setResult(null);
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
    setResult(null);
  };

  const calculateRent = async () => {
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/calculator/rent-estimate`, formData);
      setResult(response.data.results);
      toast.success("Rent estimated!");
    } catch (error) {
      toast.error("Failed to estimate rent");
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="rent-estimator-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">Rent Estimator</h1>
            </div>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              <Button 
              onClick={calculateRent}
              disabled={calculating}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              data-testid="estimate-rent-btn"
            >
              {calculating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-slate-900 mr-2"></div>
              ) : (
                <Calculator className="h-4 w-4 mr-2" />
              )}
              Estimate Rent
            </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="space-y-6">
            {/* Property Details */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Home className="h-5 w-5 text-amber-500" />
                Property Details
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Property Type</Label>
                  <Select 
                    value={formData.property_type} 
                    onValueChange={(v) => handleInputChange('property_type', v)}
                  >
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {propertyTypeOptions.map((option) => {
                        const disabled = isFreeInvestor && option.value !== "single_family";
                        return (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            disabled={disabled}
                            className={disabled ? "text-slate-600 opacity-50 cursor-not-allowed" : ""}
                          >
                            {option.label}{disabled ? " - Starter/Pro" : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-slate-300 flex items-center gap-1">
                      <Bed className="h-3 w-3" /> Bedrooms
                    </Label>
                    <Select 
                      value={formData.bedrooms.toString()} 
                      onValueChange={(v) => handleInputChange('bedrooms', parseInt(v))}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="0">Studio</SelectItem>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="3">3</SelectItem>
                        <SelectItem value="4">4</SelectItem>
                        <SelectItem value="5">5+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300 flex items-center gap-1">
                      <Bath className="h-3 w-3" /> Bathrooms
                    </Label>
                    <Select 
                      value={formData.bathrooms.toString()} 
                      onValueChange={(v) => handleInputChange('bathrooms', parseFloat(v))}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="1.5">1.5</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="2.5">2.5</SelectItem>
                        <SelectItem value="3">3</SelectItem>
                        <SelectItem value="3.5">3.5</SelectItem>
                        <SelectItem value="4">4+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300 flex items-center gap-1">
                      <Square className="h-3 w-3" /> Sq Ft
                    </Label>
                    <Input
                      type="number"
                      value={formData.square_footage}
                      onChange={(e) => handleInputChange('square_footage', parseInt(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300 flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Year Built
                    </Label>
                    <Input
                      type="number"
                      value={formData.year_built}
                      onChange={(e) => handleInputChange('year_built', parseInt(e.target.value) || 2000)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Condition</Label>
                    <Select 
                      value={formData.condition} 
                      onValueChange={(v) => handleInputChange('condition', v)}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="poor">Poor</SelectItem>
                        <SelectItem value="fair">Fair</SelectItem>
                        <SelectItem value="average">Average</SelectItem>
                        <SelectItem value="good">Good</SelectItem>
                        <SelectItem value="excellent">Excellent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-cyan-500" />
                Location Quality
              </h3>
              <div>
                <Label className="text-slate-300">Neighborhood Rating</Label>
                <Select 
                  value={formData.location_quality} 
                  onValueChange={(v) => handleInputChange('location_quality', v)}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="poor">Poor (D Class)</SelectItem>
                    <SelectItem value="fair">Fair (C Class)</SelectItem>
                    <SelectItem value="average">Average (B Class)</SelectItem>
                    <SelectItem value="good">Good (A Class)</SelectItem>
                    <SelectItem value="excellent">Excellent (A+ Class)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-2">
                  Consider: schools, crime rate, job market, walkability, amenities nearby
                </p>
              </div>
            </div>

            {/* Amenities */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-emerald-500" />
                Amenities
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {amenityOptions.map((amenity) => (
                  <div 
                    key={amenity.value}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={amenity.value}
                      checked={formData.amenities.includes(amenity.value)}
                      onCheckedChange={() => toggleAmenity(amenity.value)}
                      className="border-slate-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    />
                    <label
                      htmlFor={amenity.value}
                      className="text-sm text-slate-300 cursor-pointer"
                    >
                      {amenity.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {!result ? (
              <div className="glass-card rounded-xl p-12 text-center h-full flex flex-col items-center justify-center">
                <DollarSign className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Estimate Rental Income</h3>
                <p className="text-slate-400 mb-6">Enter property details to get a rent estimate</p>
                <Button 
                  onClick={calculateRent}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Estimate Now
                </Button>
              </div>
            ) : (
              <>
                {/* Main Estimate */}
                <div className="bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/30 rounded-xl p-6 text-center">
                  <div className="text-sm text-slate-300 mb-2">Estimated Monthly Rent</div>
                  <div className="text-5xl font-bold font-mono text-amber-500" data-testid="estimated-rent">
                    {formatCurrency(result.estimated_rent)}
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-4 text-sm">
                    <div>
                      <span className="text-slate-400">Low: </span>
                      <span className="font-mono text-slate-300">{formatCurrency(result.low_estimate)}</span>
                    </div>
                    <div className="h-4 w-px bg-slate-700"></div>
                    <div>
                      <span className="text-slate-400">High: </span>
                      <span className="font-mono text-slate-300">{formatCurrency(result.high_estimate)}</span>
                    </div>
                  </div>
                </div>

                {/* Price Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Price per Sq Ft</div>
                    <div className="text-2xl font-bold font-mono text-white">
                      ${result.price_per_sqft}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Annual Rent</div>
                    <div className="text-2xl font-bold font-mono text-cyan-500">
                      {formatCurrency(result.annual_rent)}
                    </div>
                  </div>
                </div>

                {/* Comparable Metrics */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Comparable Metrics</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Price per Bedroom</span>
                      <span className="font-mono text-white">{formatCurrency(result.comparable_metrics.price_per_bedroom)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Price per Bathroom</span>
                      <span className="font-mono text-white">{formatCurrency(result.comparable_metrics.price_per_bathroom)}</span>
                    </div>
                  </div>
                </div>

                {/* Adjustment Factors */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">How We Calculated</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-1">
                      <span className="text-slate-400">Base Rent ({formData.square_footage} sq ft)</span>
                      <span className="font-mono text-slate-300">{formatCurrency(result.adjustments.base_rent)}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-slate-400">Bedroom Adjustment</span>
                      <span className="font-mono text-slate-300">×{result.adjustments.bedroom_factor}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-slate-400">Bathroom Adjustment</span>
                      <span className="font-mono text-slate-300">×{result.adjustments.bathroom_factor}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-slate-400">Condition ({formData.condition})</span>
                      <span className="font-mono text-slate-300">×{result.adjustments.condition_factor}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-slate-400">Location ({formData.location_quality})</span>
                      <span className="font-mono text-slate-300">×{result.adjustments.location_factor}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-slate-400">Age ({new Date().getFullYear() - formData.year_built} years)</span>
                      <span className="font-mono text-slate-300">×{result.adjustments.age_factor}</span>
                    </div>
                    {result.adjustments.amenity_bonus > 0 && (
                      <div className="flex justify-between py-1">
                        <span className="text-slate-400">Amenity Bonus</span>
                        <span className="font-mono text-emerald-500">+{formatCurrency(result.adjustments.amenity_bonus)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="text-xs text-slate-500 text-center">
                  This is an estimate based on general market factors. 
                  For accurate pricing, research comparable rentals in your specific market.
                  This estimate is informational only and is not financial or investment advice.
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RentEstimator;
